'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  getCourseStructureByIdService,
  getCourseStructuresListService,
} from '@/services/httpServices/AutocourseServices';
import type {
  GetAutocourseListServiceConfig,
  GetAutocourseSingleByIdServiceConfig,
} from '@/services/httpServices/AutocourseServices/types';

import { autocourseByIdKeys, autocourseListKeys } from './autocourseQueryKeys';
import { useQueryConfig } from './useQueryConfig';

export function useAutocourseQueryList(
  params: GetAutocourseListServiceConfig,
  options?: { enabled?: boolean },
) {
  const { isEnabled } = useQueryConfig();
  const intervalCountRef = useRef(0);
  const enabled = isEnabled && options?.enabled !== false;

  const query = useQuery({
    queryKey: autocourseListKeys(params),
    queryFn: async () => getCourseStructuresListService(params),
    staleTime: 60000,
    enabled,
  });

  useEffect(() => {
    if (enabled === true && intervalCountRef.current < 2) {
      const interval = setInterval(() => {
        void query.refetch();
        intervalCountRef.current += 1;
      }, 5000);

      return () => {
        clearInterval(interval);
      };
    }
    return () => {
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return query;
}

export function useAutocourseById(params: GetAutocourseSingleByIdServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: autocourseByIdKeys(params),
    queryFn: async () => getCourseStructureByIdService(params),
    enabled: isEnabled,
  });
}
